import React from 'react';
import {
  Box, Button, Chip, IconButton, TextField, Tooltip, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DrawOutlinedIcon from '@mui/icons-material/DrawOutlined';
import TabletMacOutlinedIcon from '@mui/icons-material/TabletMacOutlined';
import RequestManager from '../utilities/requestManager';
import { StateManager } from '../utilities/stateManager';
import { StackedDialog } from '../utilities/ModalStackContext';
import SignaturePlacement, { b64ToBytes, renderPdf, normalizeFieldPage, toFieldMap } from './SignaturePlacement';
import InPersonSignDialog from './CarModal/InPersonSignDialog';

// The bespoke document hangs off either parent; only the docType differs.
const DOC_TYPES = { car: 'custom', serviceOrder: 'custom_service' };

// Builder D binds each box to a signer by role, and the backend requires a
// distinct role per signer. The role is only a match key, so generate one per
// signer row and show the signer's name in the editor instead.
let roleSeq = 0;
const newSigner = (s = {}) => ({ key: `signer${++roleSeq}`, name: s.name || '', email: s.email || '' });

const isEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

/**
 * Send an uploaded Document Library PDF for signature.
 *
 * The backend validates and normalizes the file first (prepareCustomSignatureDocument),
 * so staff place boxes on exactly the bytes that get stamped. Each signer needs at
 * least one box. The signed copy is filed back on the same Files tab.
 *
 * Props
 *   file        library file doc ({ id, name })
 *   parentType  'car' | 'serviceOrder'
 *   refId       stock number of the car or service order
 *   contacts    [{ label, name, email }] quick-add suggestions (buyer, consignor…)
 */
export default function CustomSignatureDialog({ open, onClose, file, parentType = 'car', refId, contacts = [] }) {
  const [docState, setDocState] = React.useState('loading'); // loading | ready | error
  const [errorMsg, setErrorMsg] = React.useState('');
  const [pages, setPages] = React.useState([]);
  const [fields, setFields] = React.useState([]);
  const [signers, setSigners] = React.useState([]);
  const [docName, setDocName] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [busy, setBusy] = React.useState('');
  const [inPersonUrl, setInPersonUrl] = React.useState('');
  const scaleRef = React.useRef(1);

  const sourcePath = file ? `files/${file.id}` : '';

  React.useEffect(() => {
    if (!open || !file) return;
    let cancelled = false;
    setDocState('loading'); setErrorMsg(''); setPages([]); setFields([]); setMessage(''); setInPersonUrl('');
    setDocName(String(file.name || 'Document').replace(/\.pdf$/i, ''));
    const first = contacts.find((c) => c.email) || contacts[0];
    setSigners([newSigner(first)]);
    (async () => {
      try {
        const res = await RequestManager.post({ function: 'prepareCustomSignatureDocument', variables: { sourcePath } });
        if (cancelled) return;
        if (!res || res.status !== 'complete') {
          setErrorMsg((res && res.message) || 'Could not load this document.');
          setDocState('error');
          return;
        }
        const rendered = await renderPdf(b64ToBytes(res.base64));
        if (cancelled) return;
        setPages(rendered);
        setDocState('ready');
      } catch (e) {
        if (!cancelled) { setErrorMsg('Could not load this document.'); setDocState('error'); }
      }
    })();
    return () => { cancelled = true; };
  }, [open, file]); // eslint-disable-line react-hooks/exhaustive-deps

  const roles = React.useMemo(
    () => signers.map((s, i) => ({ key: s.key, label: s.name.trim() || `Signer ${i + 1}` })),
    [signers],
  );
  const handleScale = React.useCallback((s) => { scaleRef.current = s; }, []);

  const updateSigner = (key, patch) => setSigners((p) => p.map((s) => (s.key === key ? { ...s, ...patch } : s)));
  const addSigner = (contact) => setSigners((p) => [...p, newSigner(contact)]);
  const removeSigner = (key) => {
    setSigners((p) => p.filter((s) => s.key !== key));
    setFields((p) => p.filter((f) => f.role !== key));
  };

  const unusedContacts = contacts.filter((c) => c.email && !signers.some((s) => s.email === c.email));

  const validate = ({ requireEmail }) => {
    if (!signers.length) return 'Add at least one signer.';
    if (signers.some((s) => !s.name.trim())) return 'Enter a name for every signer.';
    if (requireEmail && signers.some((s) => !isEmail(s.email.trim()))) {
      return 'Every signer needs a valid email. Or use Sign in Person.';
    }
    const empty = signers.filter((s) => !fields.some((f) => f.role === s.key));
    if (empty.length) return `Place at least one box for ${empty.map((s) => s.name.trim() || 'each signer').join(', ')}.`;
    return '';
  };

  const submit = async (inPerson) => {
    const problem = validate({ requireEmail: !inPerson });
    if (problem) { StateManager.setAlertAndOpen(problem, 'error'); return; }
    setBusy(inPerson ? 'inperson' : 'send');
    const normalized = fields.map((f) => normalizeFieldPage(f, pages, scaleRef.current));
    const name = docName.trim() || 'Document';
    const res = await RequestManager.post({
      function: 'createSignatureSession',
      variables: {
        docType: DOC_TYPES[parentType],
        refId,
        sourcePath,
        fieldMap: toFieldMap(normalized),
        signers: signers.map((s) => ({ role: s.key, name: s.name.trim(), email: s.email.trim() })),
        inPerson,
        name,
        subject: `Signature Requested - ${name}`,
        message: message.trim(),
        sentById: StateManager.userID,
      },
    });
    setBusy('');
    if (!res || res.status !== 'complete') {
      StateManager.setAlertAndOpen((res && res.message) || 'Could not send the document.', 'error');
      return;
    }
    if (inPerson) { setInPersonUrl(res.signUrl); return; }
    StateManager.setAlertAndOpen(parentType === 'serviceOrder'
      ? 'Document sent for signature. Track it on the Sends tab.'
      : 'Document sent for signature. Track it under Signature requests on this Files tab.', 'success');
    onClose();
  };

  const title = (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }} noWrap>{docName || 'Document'}</Typography>
      <Typography variant="caption" color="text.secondary">
        {fields.length} field{fields.length === 1 ? '' : 's'} · Put each box's bottom edge on the signing line.
      </Typography>
    </Box>
  );

  const toolbarExtras = (
    <>
      <Button variant="outlined" startIcon={<TabletMacOutlinedIcon />} onClick={() => submit(true)} disabled={!!busy || docState !== 'ready'}>
        {busy === 'inperson' ? 'Starting…' : 'Sign in Person'}
      </Button>
      <Button variant="contained" startIcon={<DrawOutlinedIcon />} onClick={() => submit(false)} disabled={!!busy || docState !== 'ready'}>
        {busy === 'send' ? 'Sending…' : 'Send for Signature'}
      </Button>
    </>
  );

  return (
    <>
      <StackedDialog open={open} onClose={onClose} fullScreen>
        <Box sx={{ display: 'flex', height: '100vh', bgcolor: '#fff', overflow: 'hidden' }}>
          {/* LEFT RAIL */}
          <Box sx={{ width: 300, flexShrink: 0, borderRight: '1px solid', borderColor: 'divider', bgcolor: '#fbfbfc', display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
            <Box sx={{ px: 2.5, pt: 2, pb: 1, display: 'flex', alignItems: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 700, flex: 1 }}>Request signature</Typography>
              <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
            </Box>
            <Box sx={{ px: 2.5, display: 'flex', flexDirection: 'column', gap: 2, pb: 3 }}>
              <TextField size="small" label="Document name" value={docName} onChange={(e) => setDocName(e.target.value)} />

              <Box>
                <Typography variant="overline" color="text.secondary">Signers</Typography>
                {signers.map((s, i) => (
                  <Box key={s.key} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.5, mb: 1, bgcolor: '#fff' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Typography variant="caption" sx={{ fontWeight: 600, flex: 1 }}>Signer {i + 1}</Typography>
                      {signers.length > 1 && (
                        <Tooltip title="Remove signer and their boxes">
                          <IconButton size="small" onClick={() => removeSigner(s.key)}><DeleteOutlineIcon fontSize="small" /></IconButton>
                        </Tooltip>
                      )}
                    </Box>
                    <TextField fullWidth size="small" label="Name" value={s.name} onChange={(e) => updateSigner(s.key, { name: e.target.value })} sx={{ mb: 1 }} />
                    <TextField fullWidth size="small" label="Email" value={s.email} onChange={(e) => updateSigner(s.key, { email: e.target.value })} />
                  </Box>
                ))}
                <Button size="small" startIcon={<AddIcon />} onClick={() => addSigner()}>Add signer</Button>
                {!!unusedContacts.length && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1 }}>
                    {unusedContacts.map((c) => (
                      <Chip key={c.email} size="small" icon={<AddIcon />} label={`${c.label}: ${c.name || c.email}`} onClick={() => addSigner(c)} />
                    ))}
                  </Box>
                )}
              </Box>

              <TextField size="small" label="Message (optional)" multiline minRows={3} value={message} onChange={(e) => setMessage(e.target.value)} />

              <Typography variant="caption" color="text.secondary">
                Pick a signer in the toolbar, then drag boxes onto the page. Every signer needs at least one box.
                The signed copy is added to this Files tab.
              </Typography>
            </Box>
          </Box>

          <SignaturePlacement
            pages={pages}
            fields={fields}
            onFieldsChange={setFields}
            onScaleChange={handleScale}
            roles={roles}
            state={docState}
            title={title}
            toolbarExtras={toolbarExtras}
            errorContent={(
              <Box sx={{ textAlign: 'center', mt: 8, color: 'text.secondary' }}>
                <Typography sx={{ mb: 2 }}>{errorMsg}</Typography>
                <Button onClick={onClose}>Close</Button>
              </Box>
            )}
          />
        </Box>
      </StackedDialog>

      <InPersonSignDialog
        open={!!inPersonUrl}
        onClose={() => { setInPersonUrl(''); onClose(); }}
        signUrl={inPersonUrl}
        signerName={signers[0] && signers[0].name}
      />
    </>
  );
}
